/**
 * BroadcastBanner — top-of-app banner that surfaces unread broadcasts.
 * Shows the most recent unread one with a Dismiss + optional CTA.
 */
import { useState } from "react";
import { X } from "lucide-react";
import {
  useUnreadBroadcasts,
  useDismissBroadcast,
} from "@/lib/broadcasts/useBroadcasts";

export const BroadcastBanner = () => {
  const { unread } = useUnreadBroadcasts();
  const dismiss = useDismissBroadcast();
  const [hideId, setHideId] = useState<string | null>(null);

  const next = unread.find((b) => b.id !== hideId);
  if (!next) return null;

  return (
    <div className="border-b border-border bg-foreground text-background">
      <div className="mx-auto flex max-w-6xl items-start gap-4 px-4 py-3 md:items-center">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold truncate">{next.title}</p>
          <p className="mt-0.5 text-xs opacity-80 line-clamp-2">{next.body}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {next.cta_url && next.cta_label ? (
            <a
              href={next.cta_url}
              target={next.cta_url.startsWith("http") ? "_blank" : undefined}
              rel="noreferrer"
              className="rounded-full bg-background text-foreground px-3 py-1 text-xs font-medium"
              onClick={() => dismiss.mutate(next.id)}
            >
              {next.cta_label}
            </a>
          ) : null}
          <button
            type="button"
            aria-label="Dismiss"
            onClick={() => {
              setHideId(next.id);
              dismiss.mutate(next.id);
            }}
            className="rounded-full p-1 hover:bg-background/10"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
